/**
 * @Description 可视区域内围栏的绘制，目前用于点位测评和选址地图青春版中左侧地图中绘制周边配套-所有有围栏数据的类目
 * 需求来源：https://confluence.lanhanba.com/pages/viewpage.action?pageId=172099279
 */

import { FC, useEffect, useMemo, useRef } from 'react';
import { useMethods } from '@lhb/hook';
import { isArray } from '@lhb/func';
import { Typography } from 'antd';
import { polygonColor, targetALQType1, targetALQType2, targetALQType3, targetShowPolygonZoom } from './ts-config';
import { getTargetPointDirectionInMap, showMarker } from '@/common/utils/map';
// import cs from 'classnames';
import styles from './index.module.less';
import NotObscuredCardInMap from '../NotObscuredCardInMap';

interface HoverNameComProps {
  name: string;
  width: number;
};
const HoverNameCom: FC<HoverNameComProps> = ({
  width,
  name,
}) => {
  return <div className={styles.hoverNameCon}>
    <Typography.Text
      style={{ width: width }}
      ellipsis={{ tooltip: name }}>
      <span className='fn-14 c-222 ct'>
        {name}
      </span>
    </Typography.Text>
  </div>;
};

interface AmapRailOfBCProps {
  data: any[];
  mapIns: any;
  mapDom: any; // 地图容器dom
  curZoom: number; // 地图缩放级别
  curBounds: any; // 地图可视范围
  // spotEvaluationConfig?: any; // 点位测评配置
};
const AmapRailOfBC: FC<AmapRailOfBCProps> = ({
  data,
  mapIns,
  mapDom,
  curZoom,
  curBounds,
  // spotEvaluationConfig, // 选址地图青春版是没有的
}) => {
  const overlayGroupsRef: any = useRef(); // 围栏群组
  const massRef: any = useRef(); // 小区出入口海量点
  const hoverPassagewayMarker: any = useRef(); // 小区出入口hover时的覆盖物
  const isHoveringRef = useRef<any>({
    massMarker: false, // 在海量点上
    card: false, // 在hover卡片上
  });
  // const isShowPassageway = useMemo(() => {
  //   // 小区出入口标识展示配置（1-展示，2-不展示）
  //   return spotEvaluationConfig?.communityEntranceDisplay === 1;
  // }, [spotEvaluationConfig]);

  // 显示围栏的级别
  const isShowPolygonZoom = useMemo(() => {
    return curZoom > targetShowPolygonZoom;
  }, [curZoom]);
  useEffect(() => {
    // 组件卸载时，清空相关的围栏Marker
    return () => {
      methods.handleClear();
    };
  }, []);

  useEffect(() => {
    if (!mapIns) return;
    const labelMarker = new window.AMap.Marker({
      content: ' ',
      map: mapIns,
      zIndex: 40, // 低于点击后的marker卡片
    });
    hoverPassagewayMarker.current = labelMarker;
    labelMarker.on('mouseover', () => {
      isHoveringRef.current.card = true;
    });
    labelMarker.on('mouseout', () => {
      isHoveringRef.current.card = false;
      setTimeout(() => {
        // 都是false，那么就移出
        if (Object.values(isHoveringRef.current).every((item) => !item)) {
          methods.removeHoverMarker();
        }
      }, 200);
    });
  }, [mapIns]);

  useEffect(() => {
    if (!(isArray(data) && data.length)) {
      methods.handleClear();
      return;
    };
    if (!mapIns) return;
    // 显示围栏
    if (isShowPolygonZoom && curBounds) {
      // 获取可视范围内的围栏数据
      const targetData = methods.handleFilterData();
      // console.log(`targetData`, targetData);
      // https://confluence.lanhanba.com/pages/viewpage.action?pageId=172101825 小区出入口统一放出
      // 显示小区出入口时
      // if (isShowPassageway) {
      massRef.current && (massRef.current.setMap(null));
      const passagewayData = targetData.filter(item => isArray(item.exists) && item.exists.length).flatMap(item =>
        item.exists.map(point => ({
          ...point,
          lng: parseFloat(point.lng), // 转数字类型
          lat: parseFloat(point.lat)
        }))
      );
        // console.log(`passagewayData`, passagewayData);
        // 绘制出入口
      methods.handleCreateMassPoints(passagewayData);
      // }
      // 绘制可视范围内的围栏
      // 仅隐藏重叠删除记录的围栏，保留小区出入口等点位数据。
      methods.handleRenderPolygon(targetData.filter(item => item?.overlapDeleteFlag !== true));
      return;
    };
    methods.handleClear();
  }, [mapIns, isShowPolygonZoom, curBounds, data]);

  const methods = useMethods({
    handleFilterData() { // 过滤出可视范围内的
      return data.filter((polygonData: any) => {
        const { usablePolygon } = polygonData || {};
        if (isArray(usablePolygon) && usablePolygon.length) {
          for (let i = 0; i < usablePolygon.length; i++) {
            if (usablePolygon?.[i]?.[0] && usablePolygon?.[i]?.[1] && curBounds?.contains?.(new window.AMap.LngLat(usablePolygon[i][0], usablePolygon[i][1]))) {
              return true;
            }
          }
        }
        return false;
      });
    },
    /**
     * 只重新绘制新增加的围栏
     */
    handleRenderPolygon: (visibleData: any[]) => {
      // 出现在屏幕范围内的围栏
      const newAddData: any = [];
      // 已经存在的围栏ids
      const existedIds: any[] = [];
      // 遍历可视范围内的围栏数据和已绘制的围栏，查出是否有需要新绘制的围栏
      visibleData.forEach((item) => {
        let isExisted = false;
        overlayGroupsRef.current?.eachOverlay?.((overlay: any) => {
          const targetData = overlay?.getExtData?.();
          const targetId = targetData?.uid;
          const { uid } = item || {};
          if (targetId === uid) {
            isExisted = true;
            existedIds.push(targetId);
          }
        });
        // 需要新增的数据
        if (!isExisted) {
          newAddData.push(item);
        }
      });
      // 需要移除的围栏实例
      const needRemoveArr: any = [];
      // 已绘制的围栏，移除已消失在可视范围内的围栏
      if (overlayGroupsRef.current) {
        overlayGroupsRef.current?.eachOverlay?.((overlay: any) => {
          const targetId = overlay?.getExtData?.()?.uid;
          if (!existedIds.includes(targetId)) {
            needRemoveArr.push(overlay);
          }
        });
        // console.log(`needRemoveArr 移除的`, needRemoveArr);
        overlayGroupsRef.current?.removeOverlays?.(needRemoveArr);
      }

      if (newAddData.length) {
        methods.handleCreatePolygons(newAddData);
      }
    },
    handleCreatePolygons(polygons: any[]) {
      const polygonMarkers: any = [];
      polygons.forEach((polygonItem: any) => {
        let targetColor = polygonColor[0];
        const { secondAoiTypes } = polygonItem || {};
        // secondAoiTypes 可能是null
        // targetALQType1-奥乐齐商品房; targetALQType2-奥乐齐新村房 targetALQType3-商住两用房
        if (secondAoiTypes?.includes(targetALQType1)) {
          targetColor = polygonColor[1];
        } else if (secondAoiTypes?.includes(targetALQType2)) {
          targetColor = polygonColor[2];
        } else if (secondAoiTypes?.includes(targetALQType3)) {
          targetColor = polygonColor[3];
        }
        const polygonMarker = new window.AMap.Polygon({
          strokeOpacity: 1,
          strokeColor: targetColor.color,
          fillColor: targetColor.color,
          fillOpacity: targetColor.fillOpacity,
          strokeWeight: 1,
          cursor: 'pointer',
          bubble: true,
          zIndex: 12,
          path: polygonItem.usablePolygon,
          // fillColor: polygonItem.color,
          extData: polygonItem,
        });
        polygonMarkers.push(polygonMarker);
      });

      if (overlayGroupsRef.current) { // 已存在
        overlayGroupsRef.current.addOverlays?.(polygonMarkers);
      } else {
        const polygonOverlayGroups = new window.AMap.OverlayGroup(polygonMarkers); // 围栏覆盖物群组
        mapIns.add(polygonOverlayGroups); // 添加到地图
        overlayGroupsRef.current = polygonOverlayGroups;
      }
    },
    handleClear() {
      const overlayGroup = overlayGroupsRef.current;
      if (overlayGroup && mapIns) {
        // 移除群组+清空覆盖物
        mapIns?.remove?.(overlayGroup);
        overlayGroup?.clearOverlays?.();
        overlayGroupsRef.current = null;
      }
      massRef.current && (massRef.current.setMap(null));
    },
    handleCreateMassPoints(points: any[]) {
      // const massPointsStyle: any[] = []; // 海量点样式集合
      const massPoints: any[] = []; // 海量点
      points.forEach((item: any) => {
        const itemData: any = {
          ...item,
          lnglat: [+item.lng, +item.lat],
        };
        massPoints.push(itemData);
      });
      const mass = new window.AMap.MassMarks(massPoints, {
        cursor: 'pointer',
        // 同选址视角
        zooms: [targetShowPolygonZoom, 20], // 市/区级别时显示
        style: {
          url: `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/youth/icon_passageway@2x.png`,
          size: new window.AMap.Size(16, 16),
          anchor: 'center',
        },
        zIndex: 600,
      });
      // 添加到地图
      mass.setMap(mapIns);
      massRef.current = mass;
      mass.on('mouseover', methods.handleMouseover);
      mass.on('mouseout', methods.handleMouseout);
    },
    handleMouseover({ data }) {
      // console.log(`data`, data);
      isHoveringRef.current.massMarker = true;
      const lnglat = [+data.lng, +data.lat];
      // 从卡片回到海量点时不重复渲染
      const lastExtData = hoverPassagewayMarker.current?.getExtData();
      if ((data.id === lastExtData?.id) && lastExtData?.uid) return;
      // 宽度 = 字数 * 18
      const targetWidth = data?.name ? data.name.length * 13 : 50;
      const targetRes = getTargetPointDirectionInMap({
        mapIns,
        mapDom, // 地图容器
        lnglat,
        showWh: {
          width: targetWidth,
          height: 35
        },
        influencedWh: {
          top: 84, // 图例
        },
        containerDom: mapDom,
      });

      const {
        offset,
        anchor
      } = targetRes;
      let offsetYVal = offset[1];
      if (anchor.includes('bottom')) {
        offsetYVal = -6;
      }
      hoverPassagewayMarker.current.setAnchor(anchor);
      hoverPassagewayMarker.current.setOffset(new window.AMap.Pixel(offset[0], offsetYVal));
      hoverPassagewayMarker.current.setExtData(data);
      // 这里如果不加延时，卡片在切换位置时，会闪动，因为上面set的那几个参数在渲染时机的问题
      setTimeout(() => {
        hoverPassagewayMarker.current.setPosition(lnglat);
      }, 0);
      showMarker(hoverPassagewayMarker.current, <NotObscuredCardInMap
        orientationInfo={targetRes}
      >
        <HoverNameCom
          width={targetWidth}
          name={data.name}
        />
        {/* <div style={}>
          <Typography.Text
            style={{ width: targetWidth }}
            ellipsis={{ tooltip: data.name }}>
            <span className='fn-14 c-222 ct'>
              {data.name}
            </span>
          </Typography.Text>
        </div> */}
      </NotObscuredCardInMap>);
    },
    // 移出出入口海量点
    handleMouseout() {
      isHoveringRef.current.massMarker = false;
      setTimeout(() => {
        // 都是false，那么就移出
        if (Object.values(isHoveringRef.current).every((item) => !item)) {
          methods.removeHoverMarker();
        }
      }, 200);
    },
    removeHoverMarker() {
      if (mapIns && hoverPassagewayMarker.current) {
        hoverPassagewayMarker.current.setExtData({}); // 清空数据
        hoverPassagewayMarker.current.setContent(` `);
      }
    },
  });
  return (
    <></>
  );
};

export default AmapRailOfBC;
